/* ==========================================================================
   OTF Facelift — the review block's behaviour, and putting it in the sheet.

   Three jobs in one file, because all three are needed on every page and one
   script tag is cheaper than three:

   1. THE BEHAVIOUR of a review block - the star filters, the fold, the photo
      viewer. It lives here rather than inline in the Liquid because an inline
      script does not execute when markup is injected, and the sheet injects
      this same block. Exposed as window.OTFReviews.init(root) and run
      automatically over every block already on the page.

   1c. THE PROXY SUBMIT. With JavaScript the form posts to /apps/reviews
      (photos, order check, screening, filed as a Draft by itself) and falls
      back to Shopify's contact form if the proxy is absent or broken.

   1b. THE LANDING for the follow-up email and the printed card - `?review=1`
      opens the write form, `&order=N` fills the order number in, and it
      survives a tap out of a grid into the quick sheet, because the QR on a
      card cannot know which bouquet somebody was sent. Shopify Liquid cannot
      read a query string at all, which is why this is here and not in the
      snippet. Full reasoning at the function.

   2. THE SHEET. Tapping a bouquet on this theme does not open the product
      page, it opens the quick sheet that fluid-motion.js builds in JavaScript
      (round 26, CEO: it "will provide all the info and no need to go to an
      only product page"). So the reviews have to be IN it - CEO, 1 Sep 2026:
      "Why not have it on the same card of the product. Why click then go to
      another page. Too much hassle." Fair. A link was the wrong answer.

   How the sheet gets a Liquid block into a JavaScript panel: it fetches
   <Shopify.routes.root>products/<handle>?view=reviews - the locale prefix
   matters, see the note on it below - which is templates/product.reviews.liquid
   with no layout around it - a couple of KB rather than the 290 KB of a whole
   product page - and drops the result straight in. The rating line goes under
   the description where it is seen immediately; the block itself goes at the
   foot of the sheet, the same order as the product page.

   fluid-motion.js is deliberately not edited. It is 170 KB of choreography,
   and everything here attaches from the outside: if any of it fails, the
   sheet is exactly what it was before.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- 1 */
  function init(root) {
    if (!root || root.getAttribute('data-rv-ready')) return;
    root.setAttribute('data-rv-ready', '1');

    var items = [].slice.call(root.querySelectorAll('[data-rv-item]'));
    var more = root.querySelector('[data-rv-more]');
    var none = root.querySelector('[data-rv-none]');
    var FOLD = 4, open = items.length <= FOLD, star = 0, filter = 'all';

    function apply() {
      var shown = 0;
      items.forEach(function (li) {
        var ok = true;
        if (star && +li.getAttribute('data-star') !== star) ok = false;
        if (filter === 'photos' && li.getAttribute('data-photos') !== '1') ok = false;
        if (filter === 'recipient' && li.getAttribute('data-role') !== 'recipient') ok = false;
        if (ok) { shown++; li.hidden = !(open || shown <= FOLD); } else { li.hidden = true; }
      });
      if (none) none.hidden = shown !== 0;
      if (more) more.hidden = open || shown <= FOLD;
    }
    function press(list, el) {
      list.forEach(function (b) { b.setAttribute('aria-pressed', b === el ? 'true' : 'false'); });
    }

    var rows = [].slice.call(root.querySelectorAll('[data-rv-star]'));
    rows.forEach(function (b) {
      b.addEventListener('click', function () {
        var v = +b.getAttribute('data-rv-star');
        if (star === v) { star = 0; press(rows, null); } else { star = v; press(rows, b); }
        open = true; apply();
      });
    });

    var chips = [].slice.call(root.querySelectorAll('[data-rv-filter]'));
    chips.forEach(function (b) {
      b.addEventListener('click', function () {
        filter = b.getAttribute('data-rv-filter'); press(chips, b); open = true; apply();
      });
    });

    if (more) more.addEventListener('click', function () { open = true; apply(); more.hidden = true; });

    var lb = root.querySelector('[data-rv-lb]');
    if (lb) {
      /* The viewer is position:fixed, and inside the quick sheet "fixed" is
         relative to the SHEET, not the screen - the sheet is a transformed
         element, and a transform makes a containing block for fixed
         descendants. Measured 1 Sep: the viewer came out 390 x 1432, the
         height of the sheet, with the photo centred at y=716 and the whole
         thing scrolling with the sheet. Moving it to <body> puts it on the
         screen where a viewer belongs. Any earlier one left there by a
         previous sheet is removed first, so there is only ever one. */
      if (lb.closest('.fluid-sheet')) {
        var stale = document.body.querySelector(':scope > [data-rv-lb]');
        if (stale) stale.parentNode.removeChild(stale);
        document.body.appendChild(lb);
      }
      var img = lb.querySelector('img');
      root.querySelectorAll('[data-rv-shot]').forEach(function (b) {
        b.addEventListener('click', function () {
          img.src = b.getAttribute('data-rv-shot'); lb.hidden = false; lb.focus();
        });
      });
      lb.addEventListener('click', function () { lb.hidden = true; img.removeAttribute('src'); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !lb.hidden) { lb.hidden = true; img.removeAttribute('src'); }
      });
    }

    apply();
    wireForm(root);
  }

  /* ---------------------------------------------------------------- 1c
     THE FORM GOES TO THE PROXY, AND FALLS BACK TO THE CONTACT FORM.

     Phase 2 (1 Sep 2026). The review proxy at /apps/reviews takes photos,
     checks the order, screens the words and files the review as a Draft by
     itself. This posts the form there with fetch. If the proxy answers 404
     - not installed yet - or anything 5xx - broken - the form is handed
     straight back to the browser, which posts it to Shopify's contact form
     exactly as Phase 1 did (form.submit() skips this listener, so it cannot
     loop). A 422 is the customer's mistake and is shown to them. Nothing in
     here is needed for the form to work; it only makes it better.
     No regular expressions, no backslashes: this file is uploaded through a
     GraphQL block string. */
  function wireForm(root) {
    var form = root.querySelector('[data-rv-form]');
    if (!form || !window.fetch || !window.FormData || form.getAttribute('data-rv-wired')) return;
    form.setAttribute('data-rv-wired', '1');

    var t = form.querySelector('input[name="t"]');
    if (t) t.value = String(Date.now());

    var photo = form.querySelector('input[type="file"][name="photos"]');
    var pnote = form.querySelector('[data-rv-photo-note]');
    if (photo && pnote) {
      photo.addEventListener('change', function () {
        var n = photo.files ? photo.files.length : 0, big = false;
        for (var i = 0; i < n; i++) { if (photo.files[i].size > 6 * 1024 * 1024) big = true; }
        if (n > 3 || big) {
          photo.value = ''; pnote.textContent = form.getAttribute('data-photo-big') || ''; pnote.hidden = false; return;
        }
        pnote.textContent = n ? (form.getAttribute('data-photo-n') || '').split('_N_').join(String(n)) : '';
        pnote.hidden = !n;
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('button[type="submit"]');
      var box = form.querySelector('.fl-rv-form');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = form.getAttribute('data-sending') || label; }

      function restore() { if (btn) { btn.disabled = false; btn.textContent = label; } }
      function say(cls, text) {
        var old = form.querySelector('.fluid-cok, .fluid-cerr');
        if (old && old.parentNode) old.parentNode.removeChild(old);
        var p = document.createElement('p'); p.className = cls; p.textContent = text;
        if (box) box.insertBefore(p, box.firstChild);
      }
      function fallback() { restore(); form.submit(); }

      var fd;
      try { fd = new FormData(form); } catch (err) { fallback(); return; }

      fetch('/apps/reviews', {
        method: 'POST', body: fd, credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'fetch' }
      }).then(function (r) {
        if (r.status === 422) {
          return r.json().then(function () { restore(); say('fluid-cerr', form.getAttribute('data-err') || ''); }, fallback);
        }
        if (!r.ok) { fallback(); return; }
        return r.json().then(function (j) {
          if (!j || !j.ok) { fallback(); return; }
          /* done: the form goes, the thank-you stays, and the sheet's own
             scroll position is left alone */
          if (box) {
            while (box.firstChild) box.removeChild(box.firstChild);
            var ok = document.createElement('p'); ok.className = 'fluid-cok';
            ok.textContent = form.getAttribute('data-ok') || ''; box.appendChild(ok);
          }
        }, fallback);
      }).catch(fallback);
    });
  }

  function initAll(scope) {
    var els = (scope || document).querySelectorAll('[data-fl-rv]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  /* ---------------------------------------------------------------- 1b
     THE LANDING FOR THE FOLLOW-UP EMAIL.

     The message we send a few days after a bouquet is delivered has one job:
     get the customer to the form with as little work as possible. Dropping
     them at the top of a product page and hoping they scroll and find a
     collapsed <details> is how a follow-up gets 2% instead of 20%.

     So the email links to

       /products/<handle>?review=1&order=2246#otf-reviews

     and this opens the form, fills the order number in, and scrolls to it.
     The #otf-reviews anchor is on the <section> already, so even with this
     script dead the browser still lands them on the block - the whole thing
     degrades to "the page they were going to get anyway".

     THE PRINTED CARD CANNOT NAME A BOUQUET. It is bulk-printed 450gsm stock,
     250-300 in a run, so its QR cannot carry an order or a product handle. It
     lands on a grid with ?review=1 instead, and the flag has to survive the
     tap into the quick sheet - which is why openWrite is shared and why fill()
     calls it. Scan, tap the one you were sent, write.

     `order` becomes a VERIFIED ORDER badge on the published review, which is
     the real prize: it turns the follow-up into our main source of verified
     reviews instead of asking a customer to go and find an order number. The
     value came from our own email to that address, and every review is read
     by a person before it is published, so a hand-edited URL buys nothing.

     THE RATING IS NEVER PRE-SET, and no parameter for it exists. A star
     chosen for someone before they have thought about it is a rating we
     wrote, and the house rule is that we never write a customer's words or
     their score. The form opens blank.

     Written without regular expressions or backslashes on purpose: this file
     reaches the theme through a GraphQL block string, which silently eats a
     backslash. That cost a round on 1 Sep and the byte check caught it.
  */
  var QUERY = null;
  function query() {
    if (QUERY) return QUERY;
    QUERY = {};
    var q = location.search;
    if (q && q.length > 1) {
      var parts = q.slice(1).split('&');
      for (var i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (eq < 0) continue;
        var k = decodeURIComponent(parts[i].slice(0, eq).split('+').join(' '));
        var v = decodeURIComponent(parts[i].slice(eq + 1).split('+').join(' '));
        QUERY[k] = v;
      }
    }
    return QUERY;
  }
  function wantsWrite() { return query().review === '1'; }

  /* Opens the form inside one block and fills the order number in. Shared,
     because the same link has to work on a product page AND on a bouquet
     tapped out of a grid, where the block arrives later and by fetch. */
  function openWrite(block, scroll) {
    if (!block) return;
    var write = block.querySelector('.fl-rv-write');
    if (write) write.open = true;

    var order = query().order;
    if (order) {
      var field = block.querySelector('input[name="contact[Order number]"]');
      if (field && !field.value) {
        var clean = '';
        var raw = String(order).slice(0, 24);
        for (var c = 0; c < raw.length; c++) {
          var ch = raw.charAt(c);
          if ((ch >= '0' && ch <= '9') || (ch >= 'a' && ch <= 'z') ||
              (ch >= 'A' && ch <= 'Z') || ch === '#' || ch === '-') { clean += ch; }
        }
        field.value = clean;
      }
    }

    if (!scroll) return;
    /* after the layout has settled, and honouring reduced motion */
    var soft = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    setTimeout(function () {
      try { block.scrollIntoView({ block: 'start', behavior: soft ? 'smooth' : 'auto' }); }
      catch (e) { block.scrollIntoView(); }
    }, 120);
  }

  function deepLink() {
    if (!wantsWrite()) return;
    openWrite(document.querySelector('[data-fl-rv]'), true);
  }

  window.OTFReviews = { init: init, initAll: initAll, deepLink: deepLink, openWrite: openWrite };

  function start() { initAll(); deepLink(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* ---------------------------------------------------------------- 2 */
  if (!window.MutationObserver || !window.fetch) return;

  function fill(sheet) {
    var body = sheet.querySelector('.dbody');
    if (!body || body.querySelector('[data-fl-rv]')) return;
    var handle = sheet.getAttribute('data-handle');
    if (!handle) return;

    /* THE LOCALE HAS TO BE IN THIS URL. The Arabic site lives under /ar, and
       Shopify.routes.root carries that prefix ("/" or "/ar/"). A hard-coded
       "/products/..." fetched the ENGLISH fragment on the Arabic site, so the
       sheet showed "What people said" under an otherwise Arabic bouquet. The
       product page was never wrong - Liquid sees the locale there - only this
       fetch was blind to it, which is exactly the kind of thing that survives
       a source review and dies the first time someone opens the real page.
       Caught in a browser on the real preview, 1 Sep 2026. */
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') { root += '/'; }

    fetch(root + 'products/' + encodeURIComponent(handle) + '?view=reviews', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html || !sheet.isConnected) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;

        /* the rating line goes under the description, where it is seen
           without scrolling; the block goes at the foot, as on the page. */
        var line = tmp.querySelector('.fl-rvline');
        var block = tmp.querySelector('[data-fl-rv]');
        var note = body.querySelector('.fluid-dnote');
        if (line && note && note.parentNode) note.parentNode.insertBefore(line, note.nextSibling);
        if (block) {
          body.appendChild(block);
          init(block);
          /* the QR on the printed card cannot know which bouquet, so it lands
             on the grid with ?review=1 and the flag has to survive the tap. */
          if (wantsWrite()) openWrite(block, true);
        }
      })
      .catch(function () {});
  }

  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType === 1 && n.classList && n.classList.contains('fluid-sheet')) {
          try { fill(n); } catch (e) {}
        }
      }
    }
  }).observe(document.body, { childList: true });
})();